// src/components/auth/ForgotPassword.js
//
// Chunk 9b — i18n stripped, restyled to match design system.
//
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { theme } from '../../lib/theme'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const handleReset = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      setSent(true)
      setLoading(false)
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <span style={styles.logoMerq}>Merq</span>
          <span style={styles.logoVault}>Vault</span>
        </div>

        {sent ? (
          <div style={styles.successBox}>
            <div style={styles.successTitle}>Check your email</div>
            <p style={styles.successText}>
              We sent a password reset link to{' '}
              <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>{email}</strong>
            </p>
            <Link to="/login" style={styles.backLink}>
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h2 style={styles.title}>Reset password</h2>
            <p style={styles.subtitle}>
              Enter your email and we'll send you a reset link.
            </p>
            <form onSubmit={handleReset}>
              <div style={styles.field}>
                <label style={styles.label}>Email</label>
                <input
                  style={styles.input}
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              {error && <div style={styles.error}>{error}</div>}
              <button
                style={{
                  ...styles.button,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
                type="submit"
                disabled={loading}
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
            <div style={styles.back}>
              <Link to="/login" style={styles.link}>
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const c = theme.colors
const f = theme.fonts

const styles = {
  container: {
    minHeight: '100vh',
    background: c.cream,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
    fontFamily: f.body,
  },
  card: {
    background: c.white,
    borderRadius: '20px',
    padding: '40px 32px',
    width: '100%', maxWidth: '420px',
    boxShadow: '0 14px 50px rgba(30, 16, 64, 0.1)',
    fontFamily: f.body,
  },
  logo: {
    fontSize: '28px', fontWeight: 700,
    marginBottom: '24px',
    display: 'flex', alignItems: 'baseline',
    justifyContent: 'center', gap: '6px',
    letterSpacing: '-0.01em',
  },
  logoMerq: { color: c.plum },
  logoVault: {
    color: c.green,
    fontFamily: f.display,
    fontStyle: 'italic',
  },
  icon: {
    width: '48px', height: '48px',
    borderRadius: '14px',
    background: c.purpleSoft, color: c.purple,
    display: 'grid', placeItems: 'center',
    margin: '0 auto 14px',
  },
  title: {
    fontFamily: f.display,
    fontSize: '22px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.02em',
    marginBottom: '8px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '14px',
    color: c.inkMuted,
    marginBottom: '24px',
    textAlign: 'center',
  },
  field: { marginBottom: '14px' },
  label: {
    display: 'block',
    fontSize: '12px', fontWeight: 600,
    color: c.inkMuted,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    background: c.white,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '14.5px',
    outline: 'none', boxSizing: 'border-box',
    color: c.plum,
  },
  error: {
    background: c.roseSoft, color: c.rose,
    padding: '10px 12px', borderRadius: '8px',
    fontSize: '13px', marginBottom: '14px',
  },
  button: {
    width: '100%',
    padding: '13px',
    background: c.plum, color: c.cream,
    border: 'none', borderRadius: '12px',
    fontFamily: f.body, fontSize: '14.5px', fontWeight: 600,
    cursor: 'pointer',
  },
  back: {
    textAlign: 'center', marginTop: '16px',
  },
  link: {
    color: c.green,
    textDecoration: 'none',
    fontSize: '13px', fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },

  successBox: {
    textAlign: 'center', padding: '12px 0',
  },
  successIcon: {
    width: '64px', height: '64px',
    borderRadius: '18px',
    background: c.greenSoft, color: c.green,
    display: 'grid', placeItems: 'center',
    margin: '0 auto 18px',
  },
  successTitle: {
    fontFamily: f.display,
    fontSize: '22px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.02em',
    marginBottom: '10px',
  },
  successText: {
    fontSize: '14px', color: c.inkMuted,
    marginBottom: '20px', lineHeight: 1.55,
  },
  backLink: {
    color: c.green,
    textDecoration: 'none',
    fontSize: '14px', fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', gap: '6px',
  },
}
